import React, { FC, useRef, ChangeEvent } from 'react'
import { UploadIcon } from 'uiKit/icons/UploadIcon'
import { TrashIcon } from 'uiKit/icons/Icons'
import { DocupdateIcon, EmailIcon } from 'uiKit/icons/Icons'
import Tooltip from 'uiKit/Tooltip'
import { ExternalLinkIcon } from 'uiKit/icons/ExternalLinkIcon'
import { CarePlan } from 'models/ServiceUsersTypes'
import { CARE_PLAN_PACKAGE_DISABLED_REASON } from '../../helpers/carePlanAuditGate'
import {
  ActionTooltipTarget,
  CarePlanItem,
  CarePlansContainer,
  CarePlansFileInput,
  CarePlansList,
  CarePlansUploadButton,
  DeleteAction,
  ItemActionContainer,
  ItemName,
  StatusActionWrapper,
  NoItems,
  OpenInNewWindowAction,
  Divider,
  UpdateAction,
} from './CarePlans.styles'

interface Props {
  carePlans: CarePlan[]
  onDeleteAction: (id: string) => void
  onUploadAction: (e: ChangeEvent<HTMLInputElement>) => void
  onOpenInNewWindowAction: (link: string, filename?: string) => void
  /** QCS: filename grows up to the action icons. CareBrain keeps the 80px cap. */
  expandFilename?: boolean
  /** Package care-plan audit off: disable upload + delete; download stays. */
  mutationsDisabled?: boolean
  /** Makes the upload tooltip id unique per row. */
  patientId?: number
}

const CarePlans: FC<Props> = ({
  carePlans,
  onDeleteAction,
  onUploadAction,
  onOpenInNewWindowAction,
  expandFilename = false,
  mutationsDisabled = false,
  patientId,
}) => {
  const uploadInputRef = useRef(null)
  const uploadTipId = `upload-care-plan-${patientId}`

  const formatUpdatedAt = (updatedAt?: string): string => {
    if (!updatedAt) return ''
    const date = new Date(updatedAt)
    if (Number.isNaN(date.getTime())) return updatedAt

    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)

    const day = pad(date.getDate())
    const month = pad(date.getMonth() + 1)
    const year = date.getFullYear()

    let hours = date.getHours()
    const minutes = pad(date.getMinutes())
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12
    if (hours === 0) hours = 12

    const time = `${pad(hours)}:${minutes} ${ampm}`

    return `Last updated: ${day}/${month}/${year}; ${time}`
  }

  const handleUploadOnClick = () => {
    if (mutationsDisabled) return
    uploadInputRef.current.click()
  }

  return (
    <CarePlansContainer>
      <CarePlansList>
        {carePlans?.length ? (
          carePlans.map((plan, index) => {
            const isLastItem = index === carePlans.length - 1
            return (
              <>
                <CarePlanItem key={plan._id} isUploadSuccess={plan.upload_status === 'SUCCESS'}>
                  <ItemName $expand={expandFilename} data-for={`file-${plan._id}`} data-tip>
                    {plan.originalFilename}
                  </ItemName>
                  <Tooltip id={`file-${plan._id}`}>{plan.originalFilename}</Tooltip>
                  <ItemActionContainer>
                    {plan.upload_medium && plan.upload_medium === 'API' && (
                      <>
                        <StatusActionWrapper>
                          <UpdateAction data-for={`docupdate-${plan._id}`} data-tip>
                            <DocupdateIcon color={plan.upload_status === 'SUCCESS' ? '#489B00' : '#BA0000'} />
                          </UpdateAction>
                        </StatusActionWrapper>

                        <Tooltip id={`docupdate-${plan._id}`}>{formatUpdatedAt(plan.updatedAt)}</Tooltip>
                      </>
                    )}
                    {plan.upload_medium && plan.upload_medium === 'EMAIL' && (
                      <>
                        <StatusActionWrapper>
                          <UpdateAction data-for={`emailupdate-${plan._id}`} data-tip>
                            <EmailIcon color={plan.upload_status === 'SUCCESS' ? '#489B00' : '#BA0000'} />
                          </UpdateAction>
                        </StatusActionWrapper>

                        <Tooltip id={`emailupdate-${plan._id}`}>{formatUpdatedAt(plan.updatedAt)}</Tooltip>
                      </>
                    )}
                    {(!plan.upload_medium || plan.upload_medium === 'MANUAL') && (
                      <StatusActionWrapper
                        isManualUpload={!plan.upload_medium || plan.upload_medium === 'MANUAL'}
                      />
                    )}
                    {(!plan?.upload_status || plan.upload_status === 'SUCCESS') && (
                      <>
                        <ActionTooltipTarget data-for={`delete-${plan._id}`} data-tip>
                          <DeleteAction
                            disabled={mutationsDisabled}
                            onClick={() => {
                              if (mutationsDisabled) return
                              onDeleteAction(plan._id)
                            }}
                          >
                            <TrashIcon />
                          </DeleteAction>
                        </ActionTooltipTarget>
                        <Tooltip id={`delete-${plan._id}`}>
                          {mutationsDisabled ? CARE_PLAN_PACKAGE_DISABLED_REASON : 'Delete'}
                        </Tooltip>
                        <OpenInNewWindowAction
                          data-for={`open-${plan._id}`}
                          data-tip
                          onClick={() => onOpenInNewWindowAction(plan.path, plan.originalFilename)}
                        >
                          <ExternalLinkIcon />
                        </OpenInNewWindowAction>
                        <Tooltip id={`open-${plan._id}`}>Download</Tooltip>
                      </>
                    )}
                  </ItemActionContainer>
                </CarePlanItem>
                {!isLastItem && <Divider />}
              </>
            )
          })
        ) : (
          <NoItems>Attach file(s)</NoItems>
        )}
      </CarePlansList>
      <ActionTooltipTarget data-for={uploadTipId} data-tip>
        <CarePlansUploadButton onClick={handleUploadOnClick} disabled={mutationsDisabled}>
          <UploadIcon />
        </CarePlansUploadButton>
      </ActionTooltipTarget>
      {mutationsDisabled && <Tooltip id={uploadTipId}>{CARE_PLAN_PACKAGE_DISABLED_REASON}</Tooltip>}
      <CarePlansFileInput
        ref={uploadInputRef}
        onChange={onUploadAction}
        onClick={event => (event.target.value = null)}
        disabled={mutationsDisabled}
      />
    </CarePlansContainer>
  )
}

export default CarePlans
